import { Component, Input, OnChanges } from '@angular/core';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { TaskStateInWsNotif } from 'src/app/shared/models/misc.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { TagsService } from 'src/app/shared/services/tags.service';
import { TagsContext } from 'src/app/task-process/shared/tags-context.class';
import { Task } from 'src/app/tasks/shared/models';

import { FiltersService } from '../filters/filters.service';
import { TaskListConfiguration } from '../shared/models';
import { TaskListModeEnum } from '../shared/enums';

@Component({
  selector: 'app-task-in-list',
  templateUrl: './task-in-list.component.html',
  styleUrls: ['./task-in-list.component.scss'],
})
export class TaskInListComponent implements OnChanges {
  @Input() task: Task;
  @Input() taskListConfiguration: TaskListConfiguration;
  @Input() taskListMode: TaskListModeEnum;

  status = '';
  statusClass: string;
  messageClass: string;
  tagsContext: TagsContext = null;
  taskListModeEnum = TaskListModeEnum;

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private filtersService: FiltersService,
    private tagsService: TagsService
  ) {}

  ngOnChanges() {
    this.setupStatus();
    this.tagsContext = this.tagsService.getTagContextForInbox(this.task);
  }

  taskChanged(task: Task) {
    this.task = task;
    this.setupStatus();
  }

  setupStatus() {
    // reset when message status changes
    this.status = '';
    this.statusClass = '';
    this.messageClass = '';

    if (this.task.is_assigned_to_team_of_logged_user) {
      // an archived deleted message keeps its 'deleted' state
      if (
        this.task.message.is_deleted &&
        this.task.state !== TaskStateInWsNotif.Archived &&
        this.task.state !== TaskStateInWsNotif.AutoArchived
      ) {
        this.statusClass = 'deleted-state';
        this.messageClass = 'strikethrough';
        this.status = this.basicService.translate('inbox/message/status/deleted');
      } else {
        switch (this.task.state) {
          case TaskStateInWsNotif.Pending:
            this.statusClass = 'pending-state';
            break;

          case TaskStateInWsNotif.OnHold:
            this.statusClass = 'on-hold-state';

            if (this.isAssignedToMe()) {
              this.status = `${this.basicService.translate(
                'inbox/message/status/on-hold'
              )} ${this.basicService.translate('me')}`;
            } else {
              this.status = `${this.basicService.translate('inbox/message/status/on-hold')} ${
                this.task.assigned_to.realname
              }`;
            }

            break;

          case TaskStateInWsNotif.Assigned:
            this.statusClass = 'assigned-state';
            if (this.isAssignedToMe()) {
              this.status = `${this.basicService.translate(
                'inbox/message/status/assigned'
              )} ${this.basicService.translate('me')}`;
            } else {
              this.status = `${this.basicService.translate('inbox/message/status/assigned')} ${
                this.task.assigned_to.realname
              }`;
              this.messageClass = 'shaded';
            }
            break;

          case TaskStateInWsNotif.HelpRequested:
            this.statusClass = 'help-state';

            if (this.isAssignedToMe()) {
              this.status = `${this.basicService.translate(
                'inbox/message/status/help-requested'
              )} ${this.basicService.translate('me')}`;
            } else {
              this.status = `${this.basicService.translate('inbox/message/status/help-requested')} ${
                this.task.assigned_to.realname
              }`;
            }
            break;

          case TaskStateInWsNotif.HelpAnswered:
            this.statusClass = 'help-state';

            if (this.isAssignedToMe()) {
              this.status = this.basicService.translate('inbox/message/status/help-answered/to-me');
            } else {
              this.status = this.basicService.translate('inbox/message/status/help-answered/to-other', {
                name: this.task.assigned_to.realname,
              });
            }
            break;

          case TaskStateInWsNotif.Answered:
            this.statusClass = 'answered-state';

            if (this.isAssignedToMe()) {
              this.status = `${this.basicService.translate(
                'inbox/message/status/answered'
              )} ${this.basicService.translate('me')}`;
            } else {
              this.status = `${this.basicService.translate('inbox/message/status/answered')} ${
                this.task.assigned_to.realname
              }`;
            }
            break;

          case TaskStateInWsNotif.Archived:
          case TaskStateInWsNotif.AutoArchived:
            this.statusClass = 'archived-state';

            if (this.task.message.is_deleted) {
              this.messageClass = 'strikethrough';
            }

            if (this.isAssignedToMe()) {
              this.status = `${this.basicService.translate(
                'inbox/message/status/archived'
              )} ${this.basicService.translate('me')}`;
            } else {
              this.status = `${this.basicService.translate('inbox/message/status/archived')} ${
                this.task.assigned_to.realname
              }`;
            }
            break;
        }
      }
    }
    // routed to another team
    else {
      this.statusClass = 'assigned-state';
      this.messageClass = 'shaded';

      const tags = this.filtersService.getRoutingTagsForTaskListFilters();
      const team = this.task.tags.find(tag => {
        return tag.group.id === tags[0].id;
      });

      this.status = this.basicService.translate('inbox/message/status/routed', { team: team.name });
    }
  }

  private isAssignedToMe() {
    return this.task.assigned_to.id === this.alcmeonContextService.self.id;
  }
}
